import { useMutation } from "@apollo/client";
import { parseError } from "Apollo";
import Button from "components/Button";
import { FileSelector } from "components/FileSelector";
import Input from "components/Input";
import Text from "components/Text";
import useFileUploader from "hooks/useFileUploader";
import { useContext, useState } from "react";
import { BsTrash, BsUpload } from "react-icons/bs";
import { toast } from "react-toastify";
import { getOriginalFileName } from "utils/upload";
import {
  CONFIRM_PO_DELIVERY,
  RESET_PO_DELIVERY,
  SUBMIT_DELIVERY,
} from "./graphql";

import http from "axiosHttp";

import { AppContext } from "AppContainer";
import { useNavigate } from "react-router-dom";

const SubmitDeiveryForm = ({ delivery, odooCompanyId, shipment }) => {
  console.log("shipment", shipment);

  const { user } = useContext(AppContext);

  const [lines, setLines] = useState(
    delivery.move_ids_without_package.map((line) => {
      const shipmentLine = shipment?.lines.find(
        (l) => l.product.odooId === line.product_id.id,
      );
      const qty = shipmentLine ? shipmentLine.qty : 0;

      return {
        id: line.id,
        productOdooId: line.product_id.id,
        product_name: line.product_id.display_name,
        product_number: line.product_id.default_code,
        product_description: line.product_id.description_purchase,
        remain_qty: line.product_qty,
        qty,
        remark: "",
      };
    }),
  );

  let defaultHeaderInfo = {
    companyName: "",
    address: "",
    phone: "",
    fax: "",
    mobile: "",
  };
  let defaultDeliveryRemark = "";
  if (user.supplier.name === "XDD/思尔捷") {
    defaultHeaderInfo = {
      companyName: "深圳市思尔捷科技有限公司",
      address: "东莞市谢岗镇亚飞工业园E栋",
      phone: "0769-82211949",
      fax: "0769-86378455",
      mobile: "18820345756",
    };
    defaultDeliveryRemark =
      "附注：货物交收时，请务必仔细检查，有问题请五天内通知，逾期概不负责。";
  }

  const [deliveryRemark, setDeliveryRemark] = useState(defaultDeliveryRemark);
  const [headerInfo, setHeaderInfo] = useState(defaultHeaderInfo);
  const [files, setFiles] = useState([]);
  const { isUploading, startUpload } = useFileUploader((urls) => {
    setFiles([...files, ...urls]);
  });

  const [confirmDelivery, confirmDeliveryRes] = useMutation(
    CONFIRM_PO_DELIVERY,
    {
      onCompleted() {
        toast.success("交付数量确认成功！");
      },
      onError(error) {
        window.alert(parseError(error));
      },
      refetchQueries: ["FETCH_PO_DELIVERIES"],
      awaitRefetchQueries: true,
    },
  );

  const [resetDelivery, resetDeliveryRes] = useMutation(RESET_PO_DELIVERY, {
    onCompleted() {
      toast.success("已重置，请重新填写数量。");
      setLines(lines.map((line) => ({ ...line, qty: 0, remark: "" })));
      setFiles([]);
    },
    onError(error) {
      window.alert(parseError(error));
    },
    refetchQueries: ["FETCH_PO_DELIVERIES"],
    awaitRefetchQueries: true,
  });

  const navigate = useNavigate();
  const [submitDelivery, submitDeliveryRes] = useMutation(SUBMIT_DELIVERY, {
    onCompleted() {
      toast.success("提交成功! ");
      navigate("./..");
    },
    onError(error) {
      window.alert(parseError(error));
    },
    refetchQueries: ["FETCH_PO_DELIVERIES"],
    awaitRefetchQueries: true,
  });

  const hasConfirmed = shipment !== null;
  const confirmedLines = lines.filter((line) => line.qty > 0);

  function inputAllQty() {
    const newLines = lines.map((line) => ({ ...line, qty: line.remain_qty }));
    setLines(newLines);
  }

  function resetAllQty() {
    const newLines = lines.map((line) => ({ ...line, qty: 0 }));
    setLines(newLines);
  }

  function inputLineAllQty(lineId, remainQty) {
    const newLines = lines.map((line) => {
      if (line.id === lineId) {
        return { ...line, qty: remainQty };
      }
      return line;
    });
    setLines(newLines);
  }

  function confirmQtyHandler() {
    const validLines = lines.filter((line) => line.qty > 0);
    if (validLines.length === 0) {
      window.alert("请至少填写一个产品的交付数量。");
      return;
    }

    const exceedLines = validLines.filter((line) => line.qty > line.remain_qty);
    if (exceedLines.length > 0) {
      const message = exceedLines
        .map(
          (line) =>
            `产品「${line.product_name}」交付数量 ${line.qty} 不能超过未交付数量 ${line.remain_qty}`,
        )
        .join("\n");
      window.alert(`以下产品数量不合法：\n${message}`);
      return;
    }

    if (window.confirm("确认交付数量？")) {
      confirmDelivery({
        variables: {
          odooCompanyId,
          poId: delivery.purchase_id.id,
          refName: `${delivery.purchase_id.name} | ${delivery.name}`,
          shipmentLines: validLines.map((line) => ({
            productOdooId: line.productOdooId,
            qty: line.qty,
            remark: line.remark || null,
          })),
        },
      });
    }
  }

  function downloadDeliveryForm() {
    const url = `/export/supplier_po_delivery/xlsx/`;
    const filename = `${delivery.purchase_id.name}_送货单_${delivery.name}.xlsx`;
    const data = {
      po: delivery.purchase_id.name,
      po_delivery: delivery.name,
      remark: deliveryRemark || undefined,
      header: {
        company_name: headerInfo.companyName || undefined,
        address: headerInfo.address || undefined,
        phone: headerInfo.phone || undefined,
        fax: headerInfo.fax || undefined,
        mobile: headerInfo.mobile || undefined,
      },
      lines: shipment.lines.map((line) => {
        const localLine = lines.find(
          (l) => l.productOdooId === line.product.odooId,
        );
        return {
          number: line.product.number,
          name: line.product.nameCn,
          qty: line.qty,
          remark: localLine?.remark || undefined,
        };
      }),
    };
    http
      .post(
        url,
        { data },
        {
          baseURL: process.env.REACT_APP_SERVER_BASE_URL,
          timeout: 60000,
          responseType: "blob",
          withCredentials: true,
        },
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        window.alert(String(error));
      });
  }

  function resetHandler() {
    if (
      window.confirm("确定要重新填写数量吗？重新填写需要重新下载并上传送货单。")
    ) {
      resetDelivery({
        variables: { shipmentId: shipment.id },
      });
    }
  }

  function submitHandler() {
    if (
      window.confirm(
        "确认提交吗？请再次确保提交的数据与实际交付的产品和数量相符。",
      )
    ) {
      submitDelivery({
        variables: {
          odooCompanyId,
          poId: delivery.purchase_id.id,
          deliveryId: delivery.id,
          data: JSON.stringify({
            lines: lines.map((line) => ({
              id: line.id,
              qty: line.qty,
              product_number: line.product_number,
            })),
            files,
          }),
        },
      });
    }
  }

  return (
    <div className="p-10 space-y-8">
      {/* Step 1: 确认产品数量 */}
      <div className="card">
        <h4>1. 确认产品数量</h4>
        <div className="mt-2 opacity-70">
          请在下面输入要交付的数量，提交的数量必须与实际交付的产品和数量相符。
        </div>

        <div className="-mx-2 mt-4">
          <table>
            <thead>
              <tr>
                <th>产品</th>
                <th className="text-right">未交付数量</th>
                <th className="text-right">交付数量</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.id} className="border-y border-gray-100">
                  <td className="">
                    <div>
                      <div>{line.product_name}</div>
                      <div>{line.product_description}</div>
                    </div>
                  </td>
                  <td className="text-right">{line.remain_qty}</td>
                  <td className="text-right">
                    <div className="flex items-center space-x-3">
                      <Button
                        className="text-xs"
                        disabled={hasConfirmed}
                        onClick={() =>
                          inputLineAllQty(line.id, line.remain_qty)
                        }
                      >
                        全部
                      </Button>
                      <Input
                        className="text-right w-20"
                        value={line.qty}
                        onChange={(e) => {
                          const newLines = lines.map((l) => {
                            if (l.id === line.id) {
                              return { ...l, qty: Number(e.target.value) };
                            }
                            return l;
                          });
                          setLines(newLines);
                        }}
                        disabled={hasConfirmed}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between mt-4 space-x-6">
          <div className="flex space-x-6">
            {hasConfirmed ? (
              <Button onClick={resetHandler} loading={resetDeliveryRes.loading}>
                重新填写数量
              </Button>
            ) : (
              <>
                <Button onClick={inputAllQty}>输入所有剩余数量</Button>
                <Button onClick={resetAllQty}>清空所有数量</Button>
              </>
            )}
          </div>

          <div>
            {hasConfirmed ? (
              <div>✅ 已确认交付数量，等待供应商提交送货单。</div>
            ) : (
              <Button
                onClick={confirmQtyHandler}
                loading={confirmDeliveryRes.loading}
              >
                确认交付数量
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Step 2: 下载送货单 */}
      {hasConfirmed && (
        <div className="card">
          <h4>2. 下载送货单</h4>
          <div className="mt-2 opacity-70">
            确认以下产品信息，填写备注后下载送货单。
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 pl-1">
                🏢 公司名称
              </label>
              <Input
                value={headerInfo.companyName}
                onChange={(e) =>
                  setHeaderInfo({ ...headerInfo, companyName: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 pl-1">
                📍 地址
              </label>
              <Input
                value={headerInfo.address}
                onChange={(e) =>
                  setHeaderInfo({ ...headerInfo, address: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 pl-1">
                ☎️ 电话
              </label>
              <Input
                value={headerInfo.phone}
                onChange={(e) =>
                  setHeaderInfo({ ...headerInfo, phone: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 pl-1">
                📠 传真
              </label>
              <Input
                value={headerInfo.fax}
                onChange={(e) =>
                  setHeaderInfo({ ...headerInfo, fax: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 pl-1">
                📱 手机
              </label>
              <Input
                value={headerInfo.mobile}
                onChange={(e) =>
                  setHeaderInfo({ ...headerInfo, mobile: e.target.value })
                }
              />
            </div>
          </div>

          <div className="-mx-2 mt-4">
            <table>
              <thead>
                <tr>
                  <th>产品</th>
                  <th className="text-right">交付数量</th>
                  <th>备注</th>
                </tr>
              </thead>
              <tbody>
                {confirmedLines.map((line) => (
                  <tr key={line.id} className="border-y border-gray-100">
                    <td>
                      <div>
                        <div>{line.product_name}</div>
                        <div>{line.product_description}</div>
                      </div>
                    </td>
                    <td className="text-right">{line.qty}</td>
                    <td>
                      <Input
                        placeholder="选填"
                        value={line.remark}
                        onChange={(e) => {
                          const newLines = lines.map((l) => {
                            if (l.id === line.id) {
                              return { ...l, remark: e.target.value };
                            }
                            return l;
                          });
                          setLines(newLines);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium mb-2 pl-1">
              📝 送货单备注（选填）
            </label>
            <Text
              placeholder="填写整个送货单的备注信息"
              value={deliveryRemark}
              onChange={(e) => setDeliveryRemark(e.target.value)}
            />
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={downloadDeliveryForm}>下载送货单</Button>
          </div>
        </div>
      )}

      {/* Step 3: 上传送货单 */}
      {hasConfirmed && (
        <div className="card">
          <h4>3. 上传送货单</h4>
          <div className="mt-2 opacity-70">
            请在下载送货单后，签名盖章并上传。
          </div>

          {files.length > 0 && (
            <div className="text-sm mt-2">
              {files.map((url, index) => (
                <div key={index} className="flex items-center space-x-2 mt-2">
                  <Button
                    danger
                    onClick={() =>
                      setFiles(files.filter((_, findex) => findex !== index))
                    }
                  >
                    <BsTrash />
                  </Button>
                  <a href={url} target="_blank" rel="noreferrer">
                    {getOriginalFileName(url)}
                  </a>
                </div>
              ))}
            </div>
          )}

          <FileSelector
            className="mt-2"
            uploading={isUploading}
            title={
              <div className="flex items-center space-x-2">
                <span>
                  <BsUpload />
                </span>
                <span>上传盖章的送货单</span>
              </div>
            }
            onChange={(e) => {
              const files = [...e.target.files];
              startUpload(files);
              e.target.value = null;
            }}
          />

          <div className="flex justify-end mt-4">
            <Button
              primary
              onClick={submitHandler}
              loading={submitDeliveryRes.loading}
              disabled={files.length === 0}
            >
              确认提交
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmitDeiveryForm;
